// ABOUTME: Table row component for displaying baking tasks in the new design.
// ABOUTME: Shows product name, category, quantity, priority, stock, prep time, and deadline.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { colors } from '../../theme/colors';
import { formatHorizon, formatTime } from '../../utils/formatters';
import MemberBreakdown from './MemberBreakdown';

function TaskTableRow({ task, showCategory = true, variant = 'planned', onProductSelect, testModeEnabled = false }) {
  const { t } = useTranslation();
  const [membersExpanded, setMembersExpanded] = useState(false);
  const hasMembers = task.members?.length > 0;

  const formatDuration = (minutes) => {
    if (minutes == null) return '-';
    return `${minutes} ${t('common.minutesShort')}`;
  };

  const getStartTimeText = () => {
    if (task.start_time) {
      return formatTime(task.start_time);
    }
    return '-';
  };

  const getDepletesAtText = () => {
    if (task.stock_already_depleted) {
      return t('dashboard.schedule.urgent.depleted');
    }
    if (task.stock_depletes_at) {
      return formatTime(task.stock_depletes_at);
    }
    return '-';
  };

  const getDepletesAtColor = () => {
    if (task.stock_already_depleted) {
      return colors.status.error;
    }
    return colors.text.primary;
  };

  // Urgent variant: show depletion time or "depleted" if already out of stock
  const getDeadlineText = () => {
    if (task.stock_already_depleted) {
      return t('dashboard.schedule.urgent.depleted');
    }
    if (task.stock_depletes_at) {
      return formatTime(task.stock_depletes_at);
    }
    return '-';
  };

  // When category is hidden (grouped mode), we use different column widths
  // Planned variant has 9 columns (with horizon + start time + depletes at)
  // Urgent variant has 8 columns (with horizon + single deadline column)
  const getGridColumns = () => {
    if (variant === 'planned') {
      return showCategory
        ? '3fr 1.5fr 1.5fr 1fr 1fr 1fr 0.7fr 0.7fr 0.7fr'
        : '3fr 1.5fr 1fr 1fr 1fr 0.7fr 0.7fr 0.7fr';
    }
    // urgent variant - 8 columns
    return showCategory
      ? '3fr 1.5fr 1.5fr 1fr 1fr 0.7fr 0.7fr 0.7fr'
      : '3fr 1.5fr 1fr 1fr 0.7fr 0.7fr 0.7fr';
  };

  const gridColumns = getGridColumns();

  const categoryColor = task.category?.color || colors.category.default;

  const handleClick = () => {
    // Virtual shop rows expand to the member breakdown instead of opening the chart
    if (hasMembers) {
      setMembersExpanded((expanded) => !expanded);
      return;
    }
    if (onProductSelect && task.product_id) {
      onProductSelect(task.product_id);
    }
  };

  return (
    <>
    <Box
      onClick={handleClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        alignItems: 'center',
        py: 1.5,
        px: 2,
        borderBottom: `1px solid ${colors.border.lighter}`,
        cursor: onProductSelect || hasMembers ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: colors.background.hover,
        },
      }}
    >
      {/* Product Name */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: '40px',
        }}
      >
        {/* Category color indicator */}
        <Box
          sx={{
            width: '6px',
            height: '32px',
            borderRadius: '3px',
            bgcolor: categoryColor,
            flexShrink: 0,
            ml: -0.5,
          }}
        />
        {hasMembers && (
          <ExpandMoreIcon
            sx={{
              fontSize: '1.1rem',
              color: colors.text.secondary,
              flexShrink: 0,
              transform: membersExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}
          />
        )}
        {task.product_id && (
          <Box
            sx={{
              fontSize: '0.75rem',
              color: colors.text.muted,
              flexShrink: 0,
            }}
          >
            #{task.product_id}
          </Box>
        )}
        {task.config_warning === 'missing_params' ? (
          <Box sx={{ fontSize: '0.8rem', color: colors.status.warning, fontStyle: 'italic' }}>
            {t('warnings.missingParams')}
          </Box>
        ) : (
          <Box
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '0.875rem',
              lineHeight: 1.3,
            }}
          >
            {task.product_name}
          </Box>
        )}
        {task.name_mismatch && (
          <Tooltip title={t('warnings.nameMismatch')}>
            <WarningAmberIcon sx={{ fontSize: '1rem', color: colors.status.warning, flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      {/* Category - only show when showCategory is true */}
      {showCategory && (
        <Box sx={{ textAlign: 'center', fontSize: '0.875rem', color: colors.text.secondary }}>
          {task.config_warning === 'missing_category' ? (
            <Box component="span" sx={{ color: colors.status.warning, fontStyle: 'italic' }}>
              {t('warnings.missingCategory')}
            </Box>
          ) : (
            task.category?.name || '-'
          )}
        </Box>
      )}

      {/* Quantity */}
      <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <Box component="span">
          {/* The bake quantity is the number bakers act on - oversized for readability */}
          <Box component="span" sx={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>{task.quantity ?? '-'}</Box> <Box component="span" sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('common.unitsShort')}</Box>
        </Box>
        {task.batch_size_mismatch && (
          <Tooltip title={t('warnings.batchSizeMismatch')}>
            <WarningAmberIcon sx={{ fontSize: '1rem', color: colors.status.warning, flexShrink: 0 }} />
          </Tooltip>
        )}
        {testModeEnabled && (
          <>
            <Box component="span" sx={{ color: colors.text.muted }}>|</Box>
            <Box component="span">
              {task.batches ?? '-'} {t('common.batchesShort')}
            </Box>
          </>
        )}
      </Box>

      {/* Prediction Horizon */}
      <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
        {formatHorizon(task.prediction_horizon_minutes)}
      </Box>

      {variant === 'planned' ? (
        <>
          {/* Start Time - only for planned variant */}
          <Box
            sx={{
              color: colors.text.primary,
              fontSize: '0.875rem',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            {getStartTimeText()}
          </Box>

          {/* Depletes At - for planned variant */}
          <Box
            sx={{
              color: getDepletesAtColor(),
              fontSize: '0.875rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            {getDepletesAtText()}
          </Box>
        </>
      ) : (
        /* Depletes At - for urgent variant */
        <Box
          sx={{
            color: colors.status.error,
            fontSize: '0.875rem',
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {getDeadlineText()}
        </Box>
      )}

      {/* Stock */}
      <Box sx={{ color: task.current_stock < 0 ? colors.status.error : colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
        {task.current_stock != null ? task.current_stock.toFixed(0) : '-'}
      </Box>

      {/* Prep Time */}
      <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
        {formatDuration(task.prep_time)}
      </Box>

      {/* Priority */}
      <Box sx={{ color: colors.text.primary, fontSize: '0.875rem', textAlign: 'center' }}>
        {task.urgency_score?.toFixed(0) ?? '-'}
      </Box>
    </Box>
    {hasMembers && membersExpanded && (
      <MemberBreakdown members={task.members} parentName={task.product_name} variant="task" />
    )}
    </>
  );
}

export default TaskTableRow;
